import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Save, Download } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INVOICE_TEMPLATES } from "@/lib/templates";
import { TemplatePicker } from "@/components/TemplatePicker";
import { TemplateEditDialog } from "@/components/TemplateEditDialog";
import { CustomerCard, DatesCard, ItemsCard, VatCard, SummaryCard } from "@/components/invoice-editor/parts";
import { useInvoiceForm, validateInvoice, buildPayload } from "@/components/invoice-editor/useInvoiceForm";

const getJson = (url) => async () => (await api.get(url)).data;

function StornoBanner({ number }) {
  return (
    <div className="bg-slate-100 border border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-600" data-testid="storno-banner">
      Diese Rechnung ist <span className="font-semibold text-slate-800">storniert</span> und archiviert. Sie kann nicht mehr bearbeitet werden, behält aber ihre Nummer{" "}
      <span className="font-mono">{number}</span>. Zum Reaktivieren „Storno aufheben" in der Rechnungsliste nutzen.
    </div>
  );
}

function EditorHeader({ title, sourceNumber, number, onBack, onPdf, onSave, saveDisabled, saveLabel }) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="h-9 w-9 grid place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="back-button">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
          {sourceNumber && <p className="text-xs text-slate-500" data-testid="duplicate-source">Kopie von {sourceNumber} – Daten auf heute gesetzt</p>}
          <p className="text-sm text-slate-500 font-mono">{number || "…"}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {onPdf && (
          <button onClick={onPdf} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm" data-testid="open-pdf-button">
            <Download className="h-4 w-4" /> PDF
          </button>
        )}
        <button onClick={onSave} disabled={saveDisabled} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm" data-testid="save-invoice-button">
          <Save className="h-4 w-4" /> {saveLabel}
        </button>
      </div>
    </div>
  );
}

export default function InvoiceEditor() {
  const { readOnly } = useAuth();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [params] = useSearchParams();
  const fromId = !isEdit ? params.get("from") : null;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getJson("/settings") });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: getJson("/customers") });
  const { data: nextNum } = useQuery({ queryKey: ["next-number"], queryFn: getJson("/invoices/next-number"), enabled: !isEdit });
  const { data: existing } = useQuery({ queryKey: ["invoice", id], queryFn: getJson(`/invoices/${id}`), enabled: isEdit });
  const { data: source } = useQuery({ queryKey: ["invoice", fromId], queryFn: getJson(`/invoices/${fromId}`), enabled: Boolean(fromId) });
  const { data: customTemplates = [] } = useQuery({ queryKey: ["templates"], queryFn: getJson("/templates"), enabled: !isEdit });

  const form = useInvoiceForm({ base: existing || source, isCopy: !existing, settings, applyDefaults: !isEdit && !fromId });
  const { customer, setCustomer, items, setItems, f, set, totals, applyTemplate } = form;
  const [templateKey, setTemplateKey] = useState("");
  const [editingTpl, setEditingTpl] = useState(null);
  const [busy, setBusy] = useState(false);

  const locked = existing?.status === "storniert";
  const tpl = INVOICE_TEMPLATES.find((t) => t.key === templateKey);

  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    if (c) setCustomer({ company_name: c.company_name, contact: c.contact, street: c.street, zip: c.zip, city: c.city, email: c.email, vat_id: c.vat_id });
  };

  const pickTemplate = (key) => {
    const c = key.startsWith("c:") ? customTemplates.find((x) => `c:${x.id}` === key) : null;
    const t = c
      ? { items: c.items.map((i) => ({ ...i, quantity: Number(i.quantity) ? i.quantity : "" })), referenceLabel: c.reference_label, vatRate: c.vat_rate, note: c.note, klein: c.kleinunternehmer }
      : INVOICE_TEMPLATES.find((x) => x.key === key);
    setTemplateKey(key);
    applyTemplate(t);
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Vorlage „${t.name}“ löschen?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      qc.invalidateQueries({ queryKey: ["templates"] });
      if (templateKey === `c:${t.id}`) setTemplateKey("");
      toast.success("Vorlage gelöscht");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const save = async () => {
    const err = validateInvoice(customer, items, f);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const payload = buildPayload(customer, items, f);
      const res = isEdit ? await api.put(`/invoices/${id}`, payload) : await api.post("/invoices", payload);
      qc.setQueryData(["invoice", res.data.id], res.data);
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success(isEdit ? "Rechnung gespeichert" : `Rechnung ${res.data.number} erstellt`);
      navigate(`/invoices/${res.data.id}/print`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="invoice-editor-page">
      {locked && <StornoBanner number={existing?.number} />}
      <EditorHeader
        title={isEdit ? "Rechnung bearbeiten" : fromId ? "Rechnung duplizieren" : "Neue Rechnung"}
        sourceNumber={source?.number}
        number={isEdit ? existing?.number : nextNum?.number}
        onBack={() => navigate("/invoices")}
        onPdf={isEdit ? () => navigate(`/invoices/${id}/print`) : null}
        onSave={save}
        saveDisabled={busy || locked || readOnly}
        saveLabel={busy ? "Speichern…" : locked ? "Storniert" : "Speichern & PDF"}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-6">
          {!isEdit && !fromId && <TemplatePicker active={templateKey} onPick={pickTemplate} custom={customTemplates} onDelete={deleteTemplate} onEdit={setEditingTpl} />}
          {editingTpl && <TemplateEditDialog tpl={editingTpl} onClose={() => setEditingTpl(null)} />}
          <CustomerCard customer={customer} setCustomer={setCustomer} customers={customers} onPick={pickCustomer} />
          <DatesCard f={f} set={set} placeholder={tpl?.referencePlaceholder} />
          <ItemsCard items={items} setItems={setItems} extras={tpl?.extras} />
          <VatCard f={f} set={set} />
        </div>
        <div className="lg:col-span-4 lg:sticky lg:top-20">
          <SummaryCard {...totals} klein={f.klein} vatRate={f.vatRate} />
        </div>
      </div>
    </div>
  );
}
